import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Layers } from "lucide-react";
import { formatThousands, unformatThousands, formatMoney } from "@/lib/detailSchemas";

const FLOOR_OPTIONS = [
  "2. Bodrum",
  "1. Bodrum",
  "Zemin",
  "Asma",
  "1. Kat",
  "2. Kat",
  "Manuel Giriş",
];

const num = (v) => {
  const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const unitPriceStr = (alan, deger) => {
  const a = num(alan);
  const d = num(deger);
  if (a > 0 && d > 0) return (d / a).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
  return "—";
};

// Standalone formula row: [alan] m² × [birim] ₺/m² = [deger] ₺
// Units are OUTSIDE the input boxes.
function FormulaRow({ alan, deger, onAlan, onDeger, accent, testIdPrefix }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1.4fr)] gap-2 items-center">
      <div className="flex items-baseline gap-1.5 min-w-0">
        <Input
          type="text"
          inputMode="numeric"
          value={formatThousands(alan ?? "")}
          onChange={(e) => onAlan(unformatThousands(e.target.value))}
          placeholder="0"
          className="h-10 rounded-sm text-base font-bold text-zinc-900 text-right min-w-0"
          data-testid={`${testIdPrefix}-alan-input`}
        />
        <span className="text-xs font-semibold text-zinc-600 whitespace-nowrap">m²</span>
      </div>
      <span className="text-zinc-500 text-base font-bold">×</span>
      <div className="flex items-baseline gap-1.5 min-w-0">
        <div
          className="flex-1 h-10 rounded-sm border border-dashed border-zinc-300 bg-zinc-50 px-2.5 flex items-center justify-end text-base font-bold text-zinc-800"
          title="Otomatik: Değer / Alan"
          data-testid={`${testIdPrefix}-unit-price`}
        >
          {unitPriceStr(alan, deger)}
        </div>
        <span className="text-xs font-semibold text-zinc-600 whitespace-nowrap">₺/m²</span>
      </div>
      <span className="text-zinc-500 text-base font-bold">=</span>
      <div className="flex items-baseline gap-1.5 min-w-0">
        <Input
          type="text"
          inputMode="numeric"
          value={formatThousands(deger ?? "")}
          onChange={(e) => onDeger(unformatThousands(e.target.value))}
          placeholder="0"
          className={`h-10 rounded-sm text-base font-extrabold text-zinc-900 text-right min-w-0 border-l-4 ${accent}`}
          data-testid={`${testIdPrefix}-deger-input`}
        />
        <span className="text-xs font-semibold text-zinc-600 whitespace-nowrap">₺</span>
      </div>
    </div>
  );
}

function FloorRow({ floor, onChange, onRemove, accent, testIdPrefix, idx }) {
  const isManual =
    floor.kat === "Manuel Giriş" || (floor.kat && !FLOOR_OPTIONS.includes(floor.kat));
  return (
    <div className="grid grid-cols-[130px_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1.4fr)_auto] gap-1.5 items-center bg-white border border-zinc-200 rounded-sm p-1.5">
      {isManual ? (
        <Input
          value={floor.kat === "Manuel Giriş" ? "" : floor.kat}
          onChange={(e) => onChange({ ...floor, kat: e.target.value })}
          placeholder="Kat adı"
          className="h-9 rounded-sm text-sm font-semibold text-zinc-900"
          data-testid={`${testIdPrefix}-floor-${idx}-kat`}
        />
      ) : (
        <Select
          value={floor.kat || ""}
          onValueChange={(v) =>
            onChange({ ...floor, kat: v === "Manuel Giriş" ? "Manuel Giriş" : v })
          }
        >
          <SelectTrigger className="h-9 rounded-sm text-sm font-semibold" data-testid={`${testIdPrefix}-floor-${idx}-kat`}>
            <SelectValue placeholder="Kat seçin" />
          </SelectTrigger>
          <SelectContent>
            {FLOOR_OPTIONS.map((k) => (
              <SelectItem key={k} value={k} className="text-sm">
                {k}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <div className="flex items-baseline gap-1 min-w-0">
        <Input
          type="text"
          inputMode="numeric"
          value={formatThousands(floor.alan ?? "")}
          onChange={(e) => onChange({ ...floor, alan: unformatThousands(e.target.value) })}
          placeholder="Alan"
          className="h-9 rounded-sm text-sm font-bold text-zinc-900 text-right min-w-0"
          data-testid={`${testIdPrefix}-floor-${idx}-alan`}
        />
        <span className="text-[10px] font-semibold text-zinc-600">m²</span>
      </div>
      <span className="text-zinc-500 text-sm font-bold">×</span>
      <div className="flex items-baseline gap-1 min-w-0">
        <div
          className="flex-1 h-9 rounded-sm border border-dashed border-zinc-300 bg-zinc-50 px-2 flex items-center justify-end text-sm font-bold text-zinc-800"
          title="Otomatik: Değer / Alan"
        >
          {unitPriceStr(floor.alan, floor.deger)}
        </div>
        <span className="text-[10px] font-semibold text-zinc-600">₺/m²</span>
      </div>
      <span className="text-zinc-500 text-sm font-bold">=</span>
      <div className="flex items-baseline gap-1 min-w-0">
        <Input
          type="text"
          inputMode="numeric"
          value={formatThousands(floor.deger ?? "")}
          onChange={(e) => onChange({ ...floor, deger: unformatThousands(e.target.value) })}
          placeholder="Toplam"
          className={`h-9 rounded-sm text-sm font-extrabold text-zinc-900 text-right min-w-0 border-l-4 ${accent}`}
          data-testid={`${testIdPrefix}-floor-${idx}-deger`}
        />
        <span className="text-[10px] font-semibold text-zinc-600">₺</span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="h-9 w-9 grid place-items-center text-zinc-300 hover:text-red-600"
        aria-label="Katı kaldır"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ValueBlock({
  label,
  prefix,
  alan,
  deger,
  floors,
  onAlan,
  onDeger,
  onFloors,
  color,
  canExpand,
}) {
  const expanded = (floors?.length ?? 0) > 0;
  const totalAlan = expanded ? floors.reduce((s, f) => s + num(f.alan), 0) : num(alan);
  const totalDeger = expanded ? floors.reduce((s, f) => s + num(f.deger), 0) : num(deger);
  const totalUnit = totalAlan > 0 && totalDeger > 0 ? Math.round(totalDeger / totalAlan) : null;
  const addFloor = () =>
    onFloors([...(floors || []), { kat: "", alan: "", deger: "" }]);
  const updateFloor = (idx, f) => {
    const next = [...floors];
    next[idx] = f;
    onFloors(next);
  };
  const removeFloor = (idx) => {
    const next = floors.filter((_, i) => i !== idx);
    onFloors(next);
  };

  return (
    <div className="rounded-sm border border-zinc-200 bg-white overflow-hidden">
      {/* Header row */}
      <div className={`px-3 py-2 flex items-center gap-2 border-b border-zinc-100 ${color.headerBg}`}>
        <span className={`h-3 w-1 rounded-sm ${color.accent}`} />
        <div className={`text-[11px] uppercase tracking-widest font-bold ${color.headerText}`}>
          {label}
        </div>
      </div>

      {!expanded ? (
        <div className="p-3">
          <FormulaRow
            alan={alan}
            deger={deger}
            onAlan={onAlan}
            onDeger={onDeger}
            accent={color.totalBorder}
            testIdPrefix={prefix}
          />
        </div>
      ) : (
        <div className="p-2.5 space-y-1.5">
          <div className="grid grid-cols-[130px_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1.4fr)_auto] gap-1.5 px-2 text-[9px] uppercase tracking-widest font-bold text-zinc-500">
            <span>Kat</span>
            <span className="text-right">Alan</span>
            <span></span>
            <span className="text-right">Birim</span>
            <span></span>
            <span className="text-right">Toplam</span>
            <span></span>
          </div>
          {floors.map((f, idx) => (
            <FloorRow
              key={idx}
              idx={idx}
              floor={f}
              onChange={(nf) => updateFloor(idx, nf)}
              onRemove={() => removeFloor(idx)}
              accent={color.totalBorder}
              testIdPrefix={prefix}
            />
          ))}
          {/* Totals footer */}
          <div className="mt-2 pt-2 border-t border-zinc-200 flex items-center justify-end gap-4 px-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">
              Toplam
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-bold text-zinc-900" data-testid={`${prefix}-total-alan`}>
                {totalAlan.toLocaleString("tr-TR")}
              </span>
              <span className="text-xs font-semibold text-zinc-600">m²</span>
            </div>
            <span className="text-zinc-400 font-bold">·</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-bold text-zinc-800">
                {totalUnit ? totalUnit.toLocaleString("tr-TR") : "—"}
              </span>
              <span className="text-xs font-semibold text-zinc-600">₺/m²</span>
            </div>
            <span className="text-zinc-400 font-bold">·</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-extrabold text-zinc-900" data-testid={`${prefix}-total-deger`}>
                {totalDeger > 0 ? totalDeger.toLocaleString("tr-TR") : "0"}
              </span>
              <span className="text-xs font-semibold text-zinc-600">₺</span>
            </div>
          </div>
        </div>
      )}

      {canExpand && (
        <div className="border-t border-zinc-100 px-3 py-2 flex justify-end bg-zinc-50/70">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addFloor}
            className="h-7 text-[11px] text-zinc-700 hover:text-zinc-900 font-semibold"
            data-testid={`add-floor-${prefix}`}
          >
            <Layers className="h-3 w-3 mr-1" />
            {expanded ? "Kat Daha Ekle" : "Kat Ekle"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ValueRows({ propertyType, details, onChange }) {
  const setField = (key, value) => onChange({ ...details, [key]: value });
  const canExpand = propertyType === "is_yeri";
  const isTarla = propertyType === "tarla";

  return (
    <div className="space-y-2.5">
      {isTarla && (
        <div className="rounded-sm border border-emerald-300 bg-emerald-50 p-2.5 flex items-start gap-2">
          <span className="text-emerald-700 mt-0.5 shrink-0">ℹ</span>
          <p className="text-[11px] text-emerald-900 leading-snug">
            <strong>Değer girişi ipucu:</strong> <span className="font-semibold">Yasal Değer</span> satırına tarla değerini girin.
            Mahalinde farklı bir kullanım varsa (ör. tarla ama fiilen bahçe olarak kullanılıyor),
            <span className="font-semibold"> Mevcut Değer</span> satırına o kullanımın piyasa değerini yazabilir ve
            Açıklama alanına "mahalinde bahçe" gibi bir not ekleyebilirsiniz.
          </p>
        </div>
      )}
      <ValueBlock
        label="Yasal Değer"
        prefix="yasal"
        alan={details.yasal_alan}
        deger={details.yasal_deger}
        floors={details.yasal_floors || []}
        onAlan={(v) => setField("yasal_alan", v)}
        onDeger={(v) => setField("yasal_deger", v)}
        onFloors={(v) => setField("yasal_floors", v)}
        color={{
          headerBg: "bg-emerald-50",
          headerText: "text-emerald-800",
          accent: "bg-emerald-500",
          totalBorder: "border-l-emerald-500",
        }}
        canExpand={canExpand}
      />
      <ValueBlock
        label="Mevcut Değer"
        prefix="mevcut"
        alan={details.mevcut_alan}
        deger={details.mevcut_deger}
        floors={details.mevcut_floors || []}
        onAlan={(v) => setField("mevcut_alan", v)}
        onDeger={(v) => setField("mevcut_deger", v)}
        onFloors={(v) => setField("mevcut_floors", v)}
        color={{
          headerBg: "bg-rose-50",
          headerText: "text-rose-800",
          accent: "bg-rose-500",
          totalBorder: "border-l-rose-500",
        }}
        canExpand={canExpand}
      />
    </div>
  );
}
